import { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Grid,
  CircularProgress,
} from "@mui/material";
import { BrandButton } from "components/brand";
import { Save } from "@mui/icons-material";
import { RichTextEditor } from "components/RichTextEditor";
import { ADMIN_TEXTS } from "constants/adminTexts";
import { MediaField } from "./MediaField";
import "./AboutUsContentForm.scss";

const T = ADMIN_TEXTS.about;

const validationSchema = Yup.object({
  title: Yup.string().required(T.titleRequired).max(255, T.titleMaxLength),
  description: Yup.string()
    .required(T.descriptionRequired)
    .test("min-length", T.descriptionMinLength, (value) => {
      const text = value?.replace(/<[^>]*>/g, "").trim();
      return text && text.length >= 10;
    }),
});

export const AboutUsContentForm = ({
  onSubmit,
  onCancel = null,
  initialValues = {},
  loading = false,
}) => {
  const [media1File, setMedia1File] = useState(null);
  const [preview1Url, setPreview1Url] = useState(
    initialValues.media1Url || null,
  );
  const [isVideo1, setIsVideo1] = useState(
    initialValues.media1IsVideo || false,
  );
  const [media1Error, setMedia1Error] = useState(null);

  const [media2File, setMedia2File] = useState(null);
  const [preview2Url, setPreview2Url] = useState(
    initialValues.media2Url || null,
  );
  const [isVideo2, setIsVideo2] = useState(
    initialValues.media2IsVideo || false,
  );
  const [media2Error, setMedia2Error] = useState(null);

  const formik = useFormik({
    initialValues: {
      title: initialValues.title || "",
      description: initialValues.description || "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const m1Error = !preview1Url ? T.mediaRequired : null;
      const m2Error = !preview2Url ? T.mediaRequired : null;
      setMedia1Error(m1Error);
      setMedia2Error(m2Error);
      if (m1Error || m2Error) return;
      onSubmit({ ...values, media1File, media2File });
    },
  });

  const handleFileChange =
    (setFile, setPreview, setIsVideo, clearError) => (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setFile(file);
      setIsVideo(file.type.startsWith("video"));
      setPreview(URL.createObjectURL(file));
      clearError(null);
    };

  const handleRemove =
    (setFile, setPreview, setIsVideo, initialUrl, initialIsVideo) => () => {
      setFile(null);
      setPreview(initialUrl || null);
      setIsVideo(initialIsVideo || false);
    };

  useEffect(() => {
    setPreview1Url(initialValues.media1Url || null);
    setIsVideo1(initialValues.media1IsVideo || false);
    setMedia1File(null);
  }, [initialValues.media1Url]);

  useEffect(() => {
    setPreview2Url(initialValues.media2Url || null);
    setIsVideo2(initialValues.media2IsVideo || false);
    setMedia2File(null);
  }, [initialValues.media2Url]);

  return (
    <Card>
      <CardContent>
        <form onSubmit={formik.handleSubmit} className="about-us-content-form">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={T.titleAsterisk}
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
                placeholder={T.enterTitle}
              />
            </Grid>

            <Grid item xs={12}>
              <RichTextEditor
                value={formik.values.description}
                onChange={(value) => formik.setFieldValue("description", value)}
                onBlur={() => formik.setFieldTouched("description", true)}
                placeholder={T.enterDescription}
                label={T.descriptionAsterisk}
                required
                error={formik.errors.description}
                touched={formik.touched.description}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MediaField
                label={T.media1Asterisk}
                file={media1File}
                previewUrl={preview1Url}
                isVideo={isVideo1}
                error={media1Error}
                onFileChange={handleFileChange(
                  setMedia1File,
                  setPreview1Url,
                  setIsVideo1,
                  setMedia1Error,
                )}
                onRemove={handleRemove(
                  setMedia1File,
                  setPreview1Url,
                  setIsVideo1,
                  initialValues.media1Url,
                  initialValues.media1IsVideo,
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <MediaField
                label={T.media2Asterisk}
                file={media2File}
                previewUrl={preview2Url}
                isVideo={isVideo2}
                error={media2Error}
                onFileChange={handleFileChange(
                  setMedia2File,
                  setPreview2Url,
                  setIsVideo2,
                  setMedia2Error,
                )}
                onRemove={handleRemove(
                  setMedia2File,
                  setPreview2Url,
                  setIsVideo2,
                  initialValues.media2Url,
                  initialValues.media2IsVideo,
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Box className="form-footer">
                {onCancel && (
                  <BrandButton
                    variant="secondary"
                    onClick={onCancel}
                    disabled={loading}
                  >
                    {ADMIN_TEXTS.common.cancel}
                  </BrandButton>
                )}
                <BrandButton
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  startIcon={
                    loading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <Save />
                    )
                  }
                >
                  {loading
                    ? ADMIN_TEXTS.actions.saving
                    : ADMIN_TEXTS.common.save}
                </BrandButton>
              </Box>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};
